import React, { useEffect } from 'react';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { TextField, EmailField, SimpleShowLayout, FunctionField, translate as translateHOC } from 'react-admin';
import Header from '../../../shared/header';
import { getBusinessConf } from '../../../core/config';
import { loadAllServices } from '../../../core/services';
import ChipsArray from '../../../shared/ChipsArray';
import { associatedServicesSelector } from '../../../core/selectors';
import { isPrivateServicesActive } from '../../../helpers';
import GroupedFields from '../../../shared/custom-fields/GroupedFields';
import useGetInvoiceFrequencyFields from '../../../hooks/useGetInvoiceFrequencyFields';
import { formatSiretSiren, getBusinessCountryCode, isSiretCountry } from '../../../shared/country';

const BusinessDetailsUnconnected = ({ resource, privateFleetOptions, record, classes, translate, isAdmin }) => {
  const { config } = getBusinessConf();
  // Every business identity field follows the billing country, with the main address as fallback,
  // as the back end does.
  const showSiret = isSiretCountry(getBusinessCountryCode(record && record.address, record && record.billingAddress));

  const invoiceFrequenciesFields = useGetInvoiceFrequencyFields({
    classes,
    context: 'SHOW',
  });

  useEffect(() => {
    loadAllServices();
  }, []);

  const taxNumberCountry = config && config.taxNumberEnable && (
    <TextField
      source="taxNumberCountry"
      className={classes.field}
      classes={{ root: classes.fieldContent }}
      id="Selenium-businessDetails-taxNumberCountry"
    />
  );

  const communityTaxNumber = config && config.taxNumberEnable && (
    <TextField
      source="communityTaxNumber"
      className={classes.field}
      classes={{ root: classes.fieldContent }}
      id="Selenium-businessDetails-billingGroup-communityTaxNumber"
    />
  );

  const privateFleets = isAdmin && isPrivateServicesActive(config) && (
    <ChipsArray
      className={classes.field}
      classes={{
        root: classes.chipsArrayRoot,
        chipsContainer: classes.chipsArrayContainer,
      }}
      data={privateFleetOptions}
      label={translate('resources.business.fields.privateFleetsAvailable')}
    />
  );

  return (
    <SimpleShowLayout resource={resource} record={record}>
      <Header label="resources.business.businessDetails" id="Selenium-businessDetails-Title" />
      <TextField
        source="name"
        className={classes.field}
        classes={{ root: classes.fieldContent }}
        id="Selenium-businessDetails-Name"
      />
      <TextField
        source="legalName"
        className={classes.field}
        classes={{ root: classes.fieldContent }}
        id="Selenium-businessDetails-legalName"
      />
      <TextField
        source="phoneNumber"
        className={classes.field}
        classes={{ root: classes.fieldContent }}
        id="Selenium-businessDetails-phoneNumber"
      />
      <TextField
        source="faxNumber"
        className={classes.field}
        classes={{ root: classes.fieldContent }}
        id="Selenium-businessDetails-faxNumber"
      />
      <EmailField
        source="email"
        className={classes.field}
        classes={{ root: classes.fieldContent }}
        id="Selenium-businessDetails-Email"
      />
      <TextField
        source="vat"
        className={classes.field}
        classes={{ root: classes.fieldContent }}
        id="Selenium-businessDetails-Vat"
      />
      <TextField
        source="vatVerified"
        label="resources.business.fields.vatVerified"
        className={classes.field}
        classes={{ root: classes.fieldContent }}
        id="Selenium-businessDetails-vatVerified"
      />
      {/* EIN is hidden when the SIRET field is displayed to avoid duplicate business info. */}
      {!showSiret && (
        <TextField
          source="ein"
          className={classes.field}
          classes={{ root: classes.fieldContent }}
          id="Selenium-businessDetails-Ein"
        />
      )}
      <TextField
        source="sdicemCode"
        label="SDI / PEC"
        className={classes.field}
        classes={{ root: classes.fieldContent }}
        id="Selenium-businessDetails-sdicemCode"
      />
      {showSiret && (
        <FunctionField
          label="resources.business.fields.siretSiren"
          className={classes.field}
          classes={{ root: classes.fieldContent }}
          id="Selenium-businessDetails-siretSiren"
          render={r => formatSiretSiren(r.siretSiren)}
        />
      )}
      {/* Electronic address (BT-49): shown only when a SIRET country business has a value. */}
      {showSiret && record.electronicAddress && (
        <TextField
          source="electronicAddress"
          label="resources.business.fields.electronicAddress"
          className={classes.field}
          classes={{ root: classes.fieldContent }}
          id="Selenium-businessDetails-electronicAddress"
        />
      )}
      <TextField
        source="purchaseOrderReference"
        className={classes.field}
        classes={{ root: classes.fieldContent }}
        id="Selenium-businessDetails-purchaseOrderReference"
      />
      <TextField
        source="billingGroup.name"
        className={classes.field}
        classes={{ root: classes.fieldContent }}
        id="Selenium-businessDetails-billingGroup-Name"
      />
      <TextField
        source="businessOwner.fullName"
        className={classes.field}
        classes={{ root: classes.fieldContent }}
        id="Selenium-businessDetails-businessOwner-fullName"
      />
      <TextField
        source="status"
        className={classes.field}
        classes={{ root: classes.fieldContent }}
        id="Selenium-businessDetails-Status"
      />
      {taxNumberCountry}
      {communityTaxNumber}
      {privateFleets}
      <FunctionField
        label="resources.business.fields.locale"
        className={classes.field}
        id="Selenium-businessDetails-Language"
        render={ref =>
          config.translation.invoiceLanguage &&
          config.translation.invoiceLanguage.find(language => language.code === ref.locale)
            ? config.translation.invoiceLanguage.find(language => language.code === ref.locale).label
            : ref.locale
        }
      />
      <GroupedFields fields={invoiceFrequenciesFields} resource={resource} record={record} title="Generate invoice" />
      <FunctionField
        label="resources.business.fields.tripNotes"
        render={() => translate(`resources.business.fields.${record.mandatoryTripNotes ? 'mandatory' : 'optional'}`)}
      />
    </SimpleShowLayout>
  );
};

BusinessDetailsUnconnected.propTypes = {
  record: PropTypes.shape({}),
  classes: PropTypes.shape({
    field: PropTypes.string,
    fieldContent: PropTypes.string,
  }),
  resource: PropTypes.string,
  privateFleetOptions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      id: PropTypes.string,
    })
  ),
  translate: PropTypes.func,
  isAdmin: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
  privateFleetOptions: associatedServicesSelector(state).reduce((acc, service) => {
    if (service.visibility === 'PRIVATE') {
      acc.push({ name: service.name, id: service.id });
    }
    return acc;
  }, []),
});

export const BusinessDetails = compose(
  connect(mapStateToProps),
  translateHOC
)(BusinessDetailsUnconnected);

export const BusinessDetailsTest = BusinessDetailsUnconnected;
